import React, { createContext, useState, useEffect, useRef, useCallback, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import api, { resolveBootRefresh, initBootRefresh } from '../api/axiosConfig';
import useSessionCloseBeacon from '../hooks/useSessionCloseBeacon';
import { getTokenExpiry, isValidJWTFormat } from '../utils/authUtils';

export const AuthContext = createContext();

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5001/api').replace(/\/+$/, '');

const clearPersistedAuthState = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('token');
  localStorage.removeItem('tenantId');
  localStorage.removeItem('storeId');
  localStorage.removeItem('accountingClientId');
  localStorage.removeItem('tenantType');
  localStorage.removeItem('contextKind');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('rememberedUsername');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('selectedAccountingClient');
};

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isRefreshRevoked, setIsRefreshRevoked] = useState(false);
  const [selectedClient, setSelectedClient] = useState(() => {
    // Rehydrate from sessionStorage on first render so F5 restores the last selected client
    try {
      const stored = sessionStorage.getItem('selectedAccountingClient');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const accessTokenRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const pendingRefreshPromiseRef = useRef(null);
  const refreshFailCountRef = useRef(0);

  const setAccessToken = useCallback((token) => {
    accessTokenRef.current = token;
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
    if (token) {
      const expiryMs = getTokenExpiry(token);
      setExpiresAt(expiryMs ? Math.floor(expiryMs / 1000) : null);
    } else {
      setExpiresAt(null);
    }
  }, []);

  const getAccessToken = useCallback(() => accessTokenRef.current, []);
  const clearAccessToken = useCallback(() => {
    setAccessToken(null);
  }, [setAccessToken]);

  const refreshSession = useCallback(async () => {
    if (isRefreshingRef.current && pendingRefreshPromiseRef.current) {
      return pendingRefreshPromiseRef.current;
    }

    isRefreshingRef.current = true;

    const refreshPromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = new Error(
          response.status === 401
            ? 'Your session has expired. Please log in again.'
            : 'Service temporarily unavailable.'
        );
        error.status = response.status;
        if (response.status === 401) {
          refreshFailCountRef.current += 1;
          if (refreshFailCountRef.current >= 2) {
            setIsRefreshRevoked(true);
          }
        }
        throw error;
      }

      const data = await response.json();
      if (data?.accessToken) {
        setAccessToken(data.accessToken);
        const expiryMs = getTokenExpiry(data.accessToken);
        setExpiresAt(expiryMs ? Math.floor(expiryMs / 1000) : null);
      }
      if (data?.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      refreshFailCountRef.current = 0;
      setIsRefreshRevoked(false);
      return data;
    })();

    pendingRefreshPromiseRef.current = refreshPromise;

    try {
      return await refreshPromise;
    } finally {
      isRefreshingRef.current = false;
      pendingRefreshPromiseRef.current = null;
    }
  }, [setAccessToken]);

  useSessionCloseBeacon({ isAuthenticated, apiBaseUrl: API_BASE_URL });

  useEffect(() => {
    const refreshOnLoad = async () => {
      const storedToken =
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('token');

      if (storedToken && !isValidJWTFormat(storedToken)) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
      }

      const bootToken = getAccessToken();

      // Malformed token from stale client state should never crash boot; treat as expired.
      if (bootToken && !isValidJWTFormat(bootToken)) {
        clearAccessToken();
        clearPersistedAuthState();
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        setIsAuthReady(true);
        resolveBootRefresh();
        return;
      }

      // Skip boot refresh if no session exists (e.g. on the login page) to
      // avoid a guaranteed 401 before any credentials have been provided.
      if (!localStorage.getItem('tenantId') && !bootToken) {
        setLoading(false);
        setIsAuthReady(true);
        resolveBootRefresh();
        return;
      }

      initBootRefresh();
      setIsAuthReady(false);
      setLoading(true);
      try {
        const data = await refreshSession();
        if (!data?.accessToken || !isValidJWTFormat(data.accessToken)) {
          clearAccessToken();
          clearPersistedAuthState();
          setUser(null);
          setIsAuthenticated(false);
          return;
        }

        const decoded = jwtDecode(data.accessToken);
        const userData = localStorage.getItem('user');
        const tenantId = localStorage.getItem('tenantId');
        const storeId = localStorage.getItem('storeId');

        const resolvedTenantId = tenantId || (typeof decoded.tenant === 'string' ? decoded.tenant : decoded.tenant?._id || decoded.tenant?.toString()) || null;
        setUser({
          ...decoded,
          userData: userData ? JSON.parse(userData) : null,
          tenantId: resolvedTenantId,
          storeId: storeId || null
        });
        setIsAuthenticated(true);
      } catch {
        clearAccessToken();
        clearPersistedAuthState();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
        setIsAuthReady(true);
        resolveBootRefresh();
      }
    };

    refreshOnLoad();
  }, [clearAccessToken, getAccessToken, refreshSession]);

  const login = (token, userData, tenantId, storeId, refreshToken) => {
    if (!isValidJWTFormat(token)) {
      clearAccessToken();
      clearPersistedAuthState();
      setUser(null);
      setIsAuthenticated(false);
      return;
    }

    setAccessToken(token);
    setIsRefreshRevoked(false);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    
    if (tenantId) {
      localStorage.setItem('tenantId', tenantId);
    }
    
    if (storeId) {
      localStorage.setItem('storeId', storeId);
    }
    
    const decoded = jwtDecode(token);
    const resolvedTenantId = tenantId || (typeof decoded.tenant === 'string' ? decoded.tenant : decoded.tenant?._id || decoded.tenant?.toString()) || null;
    setUser({
      ...decoded,
      userData,
      tenantId: resolvedTenantId,
      storeId: storeId || null
    });
    setIsAuthenticated(true);
    setIsAuthReady(true);
    setLoading(false);

    try {
      localStorage.setItem('terminalActivated', '1');
      localStorage.setItem('terminalActivatedAt', new Date().toISOString());
      const resolvedTid = tenantId || (typeof decoded.tenant === 'string' ? decoded.tenant : decoded.tenant?._id?.toString()) || null;
      if (resolvedTid) localStorage.setItem('terminalTenantId', resolvedTid);
    } catch {}
  };

  const logout = useCallback(async ({ clearTerminal = true } = {}) => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch {
      // Continue client-side cleanup even if server logout fails.
    }

    setAccessToken(null);
    setIsRefreshRevoked(false);
    clearPersistedAuthState();
    setUser(null);
    setIsAuthenticated(false);
    setSelectedClient(null);

    if (clearTerminal) {
      try {
        localStorage.removeItem('terminalActivated');
        localStorage.removeItem('terminalActivatedAt');
        localStorage.removeItem('terminalTenantId');
      } catch {}
    }
  }, [setAccessToken]);

  const switchClient = useCallback(async (clientId) => {
    const res = await api.post('/auth/switch-client', { accountingClientId: clientId });
    if (res.data?.accessToken) setAccessToken(res.data.accessToken);
    if (res.data?.client) {
      setSelectedClient(res.data.client);
      sessionStorage.setItem('selectedAccountingClient', JSON.stringify(res.data.client));
    }
    return res.data;
  }, [setAccessToken]);

  const value = {
    user,
    login,
    logout,
    refreshSession,
    isRefreshing: isRefreshingRef.current,
    isRefreshRevoked,
    isAuthReady,
    isAuthenticated,
    getAccessToken,
    setAccessToken,
    expiresAt,
    isAuthLoading: loading,
    loading,
    selectedClient,
    setSelectedClient,
    switchClient,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const isTerminalActivated = () => {
  try { return localStorage.getItem('terminalActivated') === '1'; }
  catch { return false; }
};